import React, { Component } from 'react'
import { withI18n, withI18nProps } from '@lingui/react'
import { connect } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import compose from 'lodash/fp/compose'
import map from 'lodash/fp/map'

import { authorsAsString } from 'client/bookmate/helpers/author/authorsAsString'
import { getAudioBookById } from 'client/bookmate/reducers/audiobooks-reducer'
import { getImpressionsByIds } from 'client/bookmate/reducers/impressions-reducer'
import { getAllEmotions } from 'client/bookmate/reducers/emotions-reducer'

import { getCurrentSeries } from 'client/bookmate/helpers/series-helpers'
import { getSecondaryAuthors } from 'client/bookmate/helpers/author-helpers'
import { prepareDefinitionListData } from 'client/shared/helpers/definition-list-helpers'
import errorBoundary from 'client/shared/decorators/error-boundary'
import urlFor, { QueryParams } from 'shared/tools/url-helper'

import withMarketing, {
  MarketingProps,
} from 'client/shared/decorators/with-marketing'

import MetaTitle from 'client/shared/blocks/meta-title'
import { MetaDescription } from 'client/shared/blocks/meta-description'

import Linka from 'client/shared/blocks/linka'
import Spacer from 'client/shared/blocks/spacer'
import List from 'client/bookmate/blocks/list'

import ImpressionsIndex from 'client/bookmate/blocks/impressions-index'
import ResourceOtherVersionsBox from 'client/bookmate/boxes/resource-other-versions-box'
import RelatedOrSeriesEpisodes from 'client/bookmate/blocks/related-or-series-episodes'

import ExpandableText from 'client/shared/blocks/expandable-text'
import DefinitionList from 'client/shared/blocks/definition-list'
import ExternalLink from 'client/bookmate/blocks/external-link'
import Topic from 'client/bookmate/blocks/topic'
import TopicList from 'client/bookmate/blocks/topic-list'
import { UnavailableInfo } from 'client/bookmate/blocks/unavailable-info'

import SVGInline from 'react-svg-inline'
import ClockIcon from 'client/shared/icons/clock.svg'

import { AudiobookProps } from 'client/shared/types/audiobook'
import { ImpressionProps } from 'client/shared/types/impression'
import { EmotionRatingProps, EmotionProps } from 'client/shared/types/emotion'
import { Dispatch, State } from 'shared/types/redux'
import { getPublicationYear } from 'client/shared/helpers/date-helpers'

type Props = {
  audiobook: AudiobookProps
  publisherResources: AudiobookProps[]
  impressionsIndex: ImpressionProps[]
  fromSeries?: string
  emotionRating: EmotionRatingProps[]
  emotions: EmotionProps[]
  hasCurrentUserImpression: boolean
  userDevice: string
  dispatch: Dispatch
  query?: QueryParams
} & withI18nProps &
  MarketingProps

class AudiobookIndexBox extends Component<Props> {
  getHumanDuration(): JSX.Element {
    const {
      audiobook: { duration = 0 },
    } = this.props

    const hours = Math.floor(duration / 3600)
    let minutes: string | number = Math.floor((duration % 3600) / 60)
    let seconds: string | number = (duration % 3600) % 60

    minutes = minutes < 10 ? `0${minutes}` : minutes
    seconds = seconds < 10 ? `0${seconds}` : seconds

    return (
      <span className="audiobook-info__duration">
        <SVGInline svg={ClockIcon} className="audiobook-info__duration-icon" />
        <span>
          {hours}:{minutes}:{seconds}
        </span>
      </span>
    )
  }

  getSecondaryData() {
    const {
      i18n,
      audiobook: {
        series_list,
        narrators = [],
        owner_catalog_title,
        publishers = [],
        translators = [],
        publication_date,
      },
      fromSeries,
      query,
    } = this.props

    const series = getCurrentSeries(series_list, fromSeries)

    return [
      {
        resource: owner_catalog_title,
        label: i18n._('book.owner'),
        value: [owner_catalog_title],
      },
      {
        resource: publishers,
        label: i18n._('book.publishers', { count: publishers.length }),
        value: getSecondaryAuthors(publishers, query),
      },
      {
        resource: series,
        label: i18n._('book.series'),
        value: series ? (
          <Linka path={urlFor.series(series.uuid, query)}>{series.title}</Linka>
        ) : null,
      },
      {
        resource: translators,
        label: i18n._('book.translator', { count: translators.length }),
        value: getSecondaryAuthors(translators, query),
      },
      {
        resource: narrators,
        label: i18n._('book.voice', { count: narrators.length }),
        value: getSecondaryAuthors(narrators, query),
      },
      {
        resource: publication_date,
        label: i18n._('book.publication_year'),
        value: [getPublicationYear(publication_date)],
      },
    ]
  }

  renderSecondaryData() {
    const data = prepareDefinitionListData(this.getSecondaryData())

    return data.length > 0 && <DefinitionList data={data} />
  }

  renderAboutAudioBookBody(): JSX.Element | null {
    const {
      userDevice,
      audiobook,
      audiobook: {
        annotation,
        external_links,
        topics,
        series_list,
        can_be_listened,
        uuid,
        language,
      },
      fromSeries,
      publisherResources,
      query,
    } = this.props

    const isMobileSize = userDevice === 'mobile'

    return (
      <>
        {!isMobileSize && <Spacer size={8} />}
        <ExpandableText
          text={annotation}
          bookLang={language}
          maxHeight={isMobileSize ? 72 : 144}
        />
        <Spacer size={24} />
        {topics && topics.length > 0 && (
          <TopicList>
            {topics.map(topic => (
              <Topic
                {...topic}
                resource="audiobooks"
                key={topic.uuid}
                query={query}
              />
            ))}
          </TopicList>
        )}
        <Spacer size={24} />
        {!can_be_listened && <UnavailableInfo kind="audiobook" />}
        {this.getHumanDuration()}
        <Spacer />
        {audiobook && this.renderSecondaryData()}
        <Spacer size={40} />
        <RelatedOrSeriesEpisodes
          headerRank={2}
          seriesList={series_list}
          fromSeries={fromSeries}
          resourceType="audiobook"
          publisherResources={publisherResources}
          uuid={uuid}
        />
        <Spacer size={24} />
        <ResourceOtherVersionsBox
          headerRank={2}
          resourceUuid={uuid}
          resourceType="audiobook"
        />
        {external_links && external_links.length > 0 && (
          <List
            kind="index"
            type="slider"
            header="book.audiobook_media_links_title"
            headerRank={2}
          >
            {external_links.map(link => (
              <ExternalLink
                externalLink={link}
                kind={external_links.length > 1 ? 'grid' : 'list'}
              />
            ))}
          </List>
        )}
      </>
    )
  }

  render(): JSX.Element | null {
    const {
      audiobook,
      emotions,
      emotionRating,
      impressionsIndex,
      hasCurrentUserImpression,
      query,
    } = this.props

    if (!audiobook) return null

    return (
      <>
        {this.renderMeta()}
        {this.renderAboutAudioBookBody()}
        <Spacer size={64} />
        <ImpressionsIndex
          headerRank={2}
          query={query}
          resource={audiobook}
          impressionsIndex={impressionsIndex}
          emotionRating={emotionRating}
          emotions={emotions}
          hasCurrentUserImpression={hasCurrentUserImpression}
        />
        <Spacer size={24} />
      </>
    )
  }

  renderMeta(): JSX.Element | null {
    const {
      audiobook,
      audiobook: { title, authors: authorsArr, annotation },
      emotionRating,
      i18n,
    } = this.props

    if (isEmpty(audiobook)) return null

    const authors = authorsAsString(authorsArr)
    const headTitle = i18n._('book.audiobook_head_title', {
      title,
      authors,
      hasAuthors: !isEmpty(authors),
    })
    const desc = annotation || map('label', emotionRating).join(', ')
    const description = i18n._('book.meta_description_audiobook', {
      title,
      rest: desc,
    })

    return (
      <>
        <MetaTitle disableSuffix title={headTitle} />
        <MetaDescription description={description} />
      </>
    )
  }
}

const connectWrapper = connect((state: State) => ({
  userDevice: state.app.size,
  audiobook: getAudioBookById(state, state.audiobook.uuid),
  impressionsIndex: getImpressionsByIds(
    state,
    state.audiobook.impressionsIndex,
  ),
  emotionRating: state.audiobook.emotionRating,
  publisherResources: state.audiobook.publisherResources,
  emotions: getAllEmotions(state),
  hasCurrentUserImpression: Boolean(state.audiobook.currentUserImpressionUuid),
  query: state.app.storedQuery,
}))

const wrapper = compose(
  connectWrapper,
  errorBoundary,
  withMarketing,
  withI18n({ update: true }),
)

export default wrapper(AudiobookIndexBox)
